/**
 * React context + hook for the analytics spine.
 *
 * `AnalyticsProvider` attaches a transport (typically the app's
 * `ServerApiClient`) and the env-gated PostHog config to the singleton client,
 * then exposes a stable `useAnalytics()` hook. Components can also import the
 * module-level `track` / `aiFeedback` / `reportBug` directly when no provider
 * is convenient — both paths share the same singleton.
 */

import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react'
import type { AnalyticsClient } from './client'
import {
  aiFeedback as singletonAiFeedback,
  getAnalytics,
  initAnalytics,
  readPostHogConfigFromEnv,
  reportBug as singletonReportBug,
  track as singletonTrack,
} from './instance'
import type {
  AiFeedbackInput,
  AnalyticsTransport,
  BugReportInput,
  PostHogConfig,
  TrackInput,
} from './types'

export interface AnalyticsContextValue {
  client: AnalyticsClient
  track: (input: TrackInput) => void
  aiFeedback: (input: AiFeedbackInput) => void
  reportBug: (input: BugReportInput) => void
}

const AnalyticsContext = createContext<AnalyticsContextValue | null>(null)

export function AnalyticsProvider(props: {
  transport?: AnalyticsTransport | null
  posthog?: PostHogConfig | null
  distinctId?: string | null
  children: ReactNode
}) {
  const { transport = null, posthog = null, distinctId = null, children } = props

  useEffect(() => {
    initAnalytics({
      transport,
      posthog: posthog ?? readPostHogConfigFromEnv(),
      distinctId,
    })
  }, [transport, posthog, distinctId])

  const value = useMemo<AnalyticsContextValue>(
    () => ({
      client: getAnalytics(),
      track: singletonTrack,
      aiFeedback: singletonAiFeedback,
      reportBug: singletonReportBug,
    }),
    [],
  )

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>
}

/**
 * Returns the analytics API. Works with or without an `AnalyticsProvider`
 * ancestor — falls back to the module singleton so isolated components keep
 * working.
 */
export function useAnalytics(): AnalyticsContextValue {
  const ctx = useContext(AnalyticsContext)
  return useMemo<AnalyticsContextValue>(() => {
    if (ctx) return ctx
    return {
      client: getAnalytics(),
      track: singletonTrack,
      aiFeedback: singletonAiFeedback,
      reportBug: singletonReportBug,
    }
  }, [ctx])
}

/**
 * Convenience hook to attach a transport (e.g. a `ServerApiClient`) to the
 * singleton from a component that already holds one, without rendering a
 * provider. Safe to call repeatedly.
 */
export function useAttachAnalyticsTransport(
  transport: AnalyticsTransport | null | undefined,
  opts?: { distinctId?: string | null },
): void {
  const distinctId = opts?.distinctId ?? null
  useEffect(() => {
    initAnalytics({
      transport: transport ?? null,
      posthog: readPostHogConfigFromEnv(),
      distinctId,
    })
  }, [transport, distinctId])
}
